import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  LayoutDashboard,
  Users,
  FileText,
  LogOut,
  Plus,
  Search,
  CalendarIcon,
  Eye,
  UserPlus,
  Edit,
  Trash2,
  Menu,
  X,
  Image,
  Download,
  FileSpreadsheet,
  Mail,
  Check,
  Clock,
  BarChart3,
  CreditCard,
  Banknote,
  Building,
  Wallet,
  Store,
  ShoppingBag,
  ArrowUpDown,
  ClipboardList,
  Send,
  AlertCircle,
  Loader2,
  Trash,
  RotateCcw,
  UserX,
  Users2
} from 'lucide-react';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get photo URL (handles both external and local paths)
const getPhotoUrl = (filePath) => {
  if (!filePath) return '';
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
};

// Payment method labels
const PAYMENT_METHODS = {
  pending_deposit: { label: 'Αναμονή Κατάθεσης', icon: Clock },
  deposit: { label: 'Κατάθεση', icon: Building },
  card: { label: 'Κάρτα', icon: CreditCard },
  iris: { label: 'Iris', icon: Wallet },
  cash: { label: 'Μετρητά', icon: Banknote }
};

// Job type labels
const JOB_TYPES = {
  wholesale: { label: 'Χονδρική', icon: Store },
  retail: { label: 'Λιανική', icon: ShoppingBag }
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filters
  const [filterTechnician, setFilterTechnician] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest'); // newest, oldest, customer_asc, customer_desc

  // User form
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'technician' });

  // Job detail
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);

  // Export loading
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  // Assignment state
  const [assignments, setAssignments] = useState([]);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({
    customer_name: '',
    customer_phone: '',
    address_street: '',
    address_city: '',
    address_postal: '',
    visit_date: new Date(),
    visit_time: '',
    description: '',
    technician_ids: [],
    priority: 'normal',
    notes: '',
    job_type: 'retail',
    payment_method: 'cash',
    payment_comments: '',
    wholesale_customer_name: '',
    wholesale_customer_phone: '',
    needs_invoice: false,
    customer_afm: '',
    customer_email: ''
  });
  const [creatingAssignment, setCreatingAssignment] = useState(false);

  // Trash state
  const [trashJobs, setTrashJobs] = useState([]);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);

  // Edit job state
  const [editJobDialogOpen, setEditJobDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editJobForm, setEditJobForm] = useState({
    customer_name: '',
    customer_phone: '',
    address_street: '',
    address_city: '',
    address_postal: '',
    visit_date: '',
    description: '',
    work_performed: '',
    recommendations: '',
    status: 'pending',
    status_notes: '',
    job_type: 'retail',
    payment_method: 'cash',
    payment_comments: '',
    wholesale_customer_name: '',
    wholesale_customer_phone: '',
    needs_invoice: false,
    customer_afm: '',
    customer_email: ''
  });

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [filterTechnician, filterDateFrom, filterDateTo, filterCustomer, filterStatus]);

  useEffect(() => {
    if (activeTab === 'assignments') {
      fetchAssignments();
    }
  }, [activeTab]);

  // Sort jobs when sortOrder changes
  useEffect(() => {
    if (jobs.length > 0) {
      const sortedJobs = [...jobs].sort((a, b) => {
        switch (sortOrder) {
          case 'oldest':
            return new Date(a.created_at) - new Date(b.created_at);
          case 'newest':
            return new Date(b.created_at) - new Date(a.created_at);
          case 'customer_asc':
            return a.customer_name.localeCompare(b.customer_name, 'el');
          case 'customer_desc':
            return b.customer_name.localeCompare(a.customer_name, 'el');
          case 'date_asc':
            return a.visit_date.localeCompare(b.visit_date);
          case 'date_desc':
            return b.visit_date.localeCompare(a.visit_date);
          default:
            return new Date(b.created_at) - new Date(a.created_at);
        }
      });
      setJobs(sortedJobs);
    }
  }, [sortOrder]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/assignments`);
      setAssignments(res.data);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!assignmentForm.technician_ids || assignmentForm.technician_ids.length === 0) {
      alert('Παρακαλώ επιλέξτε τουλάχιστον έναν τεχνικό');
      return;
    }
    
    setCreatingAssignment(true);
    try {
      if (editingAssignment) {
        // Update existing assignment
        await axios.patch(`${API}/assignments/${editingAssignment.id}`, {
          ...assignmentForm,
          visit_date: format(assignmentForm.visit_date, 'yyyy-MM-dd')
        });
        alert('Η ανάθεση ενημερώθηκε!');
      } else {
        // Create new assignment
        await axios.post(`${API}/assignments`, {
          ...assignmentForm,
          visit_date: format(assignmentForm.visit_date, 'yyyy-MM-dd')
        });
        const techCount = assignmentForm.technician_ids.length;
        alert(`Η ανάθεση δημιουργήθηκε και στάλθηκε ειδοποίηση σε ${techCount} τεχνικό${techCount > 1 ? 'ύς' : ''}!`);
      }
      
      // Reset form
      setAssignmentForm({
        customer_name: '',
        customer_phone: '',
        address_street: '',
        address_city: '',
        address_postal: '',
        visit_date: new Date(),
        visit_time: '',
        description: '',
        technician_ids: [],
        priority: 'normal',
        notes: '',
        job_type: 'retail',
        payment_method: 'cash',
        payment_comments: '',
        wholesale_customer_name: '',
        wholesale_customer_phone: '',
        needs_invoice: false,
        customer_afm: '',
        customer_email: ''
      });
      setEditingAssignment(null);
      setAssignmentDialogOpen(false);
      fetchAssignments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα');
    } finally {
      setCreatingAssignment(false);
    }
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setAssignmentForm({
      customer_name: assignment.customer_name || '',
      customer_phone: assignment.customer_phone || '',
      address_street: assignment.address_street || '',
      address_city: assignment.address_city || '',
      address_postal: assignment.address_postal || '',
      visit_date: assignment.visit_date ? new Date(assignment.visit_date) : new Date(),
      visit_time: assignment.visit_time || '',
      description: assignment.description || '',
      technician_ids: assignment.technician_ids || [],
      priority: assignment.priority || 'normal',
      notes: assignment.notes || '',
      job_type: assignment.job_type || 'retail',
      payment_method: assignment.payment_method || 'cash',
      payment_comments: assignment.payment_comments || '',
      wholesale_customer_name: assignment.wholesale_customer_name || '',
      wholesale_customer_phone: assignment.wholesale_customer_phone || '',
      needs_invoice: assignment.needs_invoice || false,
      customer_afm: assignment.customer_afm || '',
      customer_email: assignment.customer_email || ''
    });
    setAssignmentDialogOpen(true);
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setEditJobForm({
      customer_name: job.customer_name || '',
      customer_phone: job.customer_phone || '',
      address_street: job.address_street || '',
      address_city: job.address_city || '',
      address_postal: job.address_postal || '',
      visit_date: job.visit_date || '',
      description: job.description || '',
      work_performed: job.work_performed || '',
      recommendations: job.recommendations || '',
      status: job.status || 'pending',
      status_notes: job.status_notes || '',
      job_type: job.job_type || 'retail',
      payment_method: job.payment_method || 'cash',
      payment_comments: job.payment_comments || '',
      wholesale_customer_name: job.wholesale_customer_name || '',
      wholesale_customer_phone: job.wholesale_customer_phone || '',
      needs_invoice: job.needs_invoice || false,
      customer_afm: job.customer_afm || '',
      customer_email: job.customer_email || ''
    });
    setEditJobDialogOpen(true);
  };

  const handleSaveJob = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/jobs/${editingJob.id}`, editJobForm);
      setEditJobDialogOpen(false);
      setEditingJob(null);
      fetchData();
      alert('Η εργασία ενημερώθηκε!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα ενημέρωσης');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την ανάθεση;')) return;
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`);
      fetchAssignments();
    } catch (error) {
      alert('Σφάλμα διαγραφής ανάθεσης');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterTechnician && filterTechnician !== 'all') params.append('technician_id', filterTechnician);
      if (filterDateFrom) params.append('date_from', format(filterDateFrom, 'yyyy-MM-dd'));
      if (filterDateTo) params.append('date_to', format(filterDateTo, 'yyyy-MM-dd'));
      if (filterCustomer) params.append('search', filterCustomer);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);

      const [jobsRes, usersRes, techsRes] = await Promise.all([
        axios.get(`${API}/jobs?${params.toString()}`),
        axios.get(`${API}/users`),
        axios.get(`${API}/technicians`)
      ]);

      // Apply initial sort (newest first)
      const sortedJobs = [...jobsRes.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setJobs(sortedJobs);
      setUsers(usersRes.data);
      setTechnicians(techsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateCompletedAssignments = async () => {
    if (!window.confirm('Θέλετε να μεταφέρετε όλες τις ολοκληρωμένες αναθέσεις στις εργασίες;')) {
      return;
    }
    try {
      const res = await axios.post(`${API}/assignments/migrate-completed`);
      alert(res.data.message);
      fetchAssignments();
      fetchData(); // Refresh jobs too
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.patch(`${API}/users/${editingUser.id}`, userForm);
      } else {
        await axios.post(`${API}/users`, userForm);
      }
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'technician' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα');
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setUserForm({ name: u.name, email: u.email, password: '', role: u.role });
    setUserDialogOpen(true);
  };

  const handleDeactivateUser = async (userId) => {
    if (window.confirm('Θέλετε να απενεργοποιήσετε αυτόν τον χρήστη;')) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.detail || 'Σφάλμα');
      }
    }
  };

  const handlePermanentDeleteUser = async (userId) => {
    if (window.confirm('ΠΡΟΣΟΧΗ: Θέλετε να διαγράψετε ΟΡΙΣΤΙΚΑ αυτόν τον χρήστη; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί!')) {
      try {
        await axios.delete(`${API}/users/${userId}/permanent`);
        fetchData();
        alert('Ο χρήστης διαγράφηκε οριστικά');
      } catch (error) {
        alert(error.response?.data?.detail || 'Σφάλμα διαγραφής');
      }
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Θέλετε να μεταφέρετε αυτή την εργασία στον κάδο;')) {
      try {
        await axios.delete(`${API}/jobs/${jobId}`);
        fetchData();
        fetchStats();
        alert('Η εργασία μεταφέρθηκε στον κάδο');
      } catch (error) {
        alert(error.response?.data?.detail || 'Σφάλμα διαγραφής');
      }
    }
  };

  const fetchTrash = async () => {
    try {
      const res = await axios.get(`${API}/jobs/trash`);
      setTrashJobs(res.data);
    } catch (error) {
      console.error('Failed to fetch trash:', error);
    }
  };

  const handleRestoreJob = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/restore`);
      fetchTrash();
      fetchData();
      fetchStats();
      alert('Η εργασία επαναφέρθηκε');
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα επαναφοράς');
    }
  };

  const handlePermanentDeleteJob = async (jobId) => {
    if (window.confirm('ΠΡΟΣΟΧΗ: Θέλετε να διαγράψετε ΟΡΙΣΤΙΚΑ αυτή την εργασία; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί!')) {
      try {
        await axios.delete(`${API}/jobs/${jobId}/permanent`);
        fetchTrash();
        alert('Η εργασία διαγράφηκε οριστικά');
      } catch (error) {
        alert(error.response?.data?.detail || 'Σφάλμα διαγραφής');
      }
    }
  };

  const handleViewJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/jobs/${jobId}`);
      setSelectedJob(res.data);
      setJobDialogOpen(true);
    } catch (error) {
      alert('Σφάλμα φόρτωσης εργασίας');
    }
  };

  const handleUpdateJobStatus = async (jobId, newStatus) => {
    try {
      await axios.patch(`${API}/jobs/${jobId}/status`, { status: newStatus });
      setSelectedJob(prev => ({ ...prev, status: newStatus }));
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: newStatus } : j));
      fetchStats();
    } catch (error) {
      alert('Σφάλμα ενημέρωσης κατάστασης');
    }
  };

  const clearFilters = () => {
    setFilterTechnician('all');
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setFilterCustomer('');
    setFilterStatus('all');
    setSortOrder('newest');
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = new URLSearchParams();
      if (filterTechnician && filterTechnician !== 'all') params.append('technician_id', filterTechnician);
      if (filterDateFrom) params.append('date_from', format(filterDateFrom, 'yyyy-MM-dd'));
      if (filterDateTo) params.append('date_to', format(filterDateTo, 'yyyy-MM-dd'));
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);

      const response = await axios.get(`${API}/jobs/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ergasies_${format(new Date(), 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Σφάλμα εξαγωγής Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = new URLSearchParams();
      if (filterTechnician && filterTechnician !== 'all') params.append('technician_id', filterTechnician);
      if (filterDateFrom) params.append('date_from', format(filterDateFrom, 'yyyy-MM-dd'));
      if (filterDateTo) params.append('date_to', format(filterDateTo, 'yyyy-MM-dd'));
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);

      const response = await axios.get(`${API}/jobs/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ergasies_${format(new Date(), 'yyyyMMdd')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Σφάλμα εξαγωγής PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSendDailyReport = async () => {
    setSendingReport(true);
    try {
      const res = await axios.post(`${API}/admin/send-daily-report`);
      alert(res.data.message);
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα αποστολής email');
    } finally {
      setSendingReport(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          <Check className="w-3 h-3" /> Ολοκληρώθηκε
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Clock className="w-3 h-3" /> Εκκρεμής
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Menu Button */}
      <button
        data-testid="mobile-menu-btn"
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={`admin-sidebar fixed md:static inset-y-0 left-0 w-64 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform z-40`}>
        <div className="p-6">
          <img src="/logo-white.png" alt="Raptopoulos" className="h-12 mb-4" />
          <p className="text-slate-400 text-sm">{user?.name}</p>
        </div>

        <nav className="px-4 space-y-2">
          <button
            data-testid="nav-jobs-btn"
            onClick={() => { setActiveTab('jobs'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'jobs' ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
          >
            <FileText className="w-5 h-5" />
            <span className="font-medium">Εργασίες</span>
          </button>
          <button
            data-testid="nav-assignments-btn"
            onClick={() => { setActiveTab('assignments'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'assignments' ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
          >
            <ClipboardList className="w-5 h-5" />
            <span className="font-medium">Αναθέσεις</span>
          </button>
          <button
            data-testid="nav-users-btn"
            onClick={() => { setActiveTab('users'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'users' ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
          >
            <Users className="w-5 h-5" />
            <span className="font-medium">Χρήστες</span>
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Αποσύνδεση</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 md:ml-0">
        <div className="max-w-7xl mx-auto">
          {/* Stats Cards */}
          {stats && activeTab === 'jobs' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.jobs_today}</p>
                    <p className="text-xs text-slate-500 uppercase">Σήμερα</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_jobs}</p>
                    <p className="text-xs text-slate-500 uppercase">Συνολικά</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.completed_jobs}</p>
                    <p className="text-xs text-slate-500 uppercase">Ολοκληρωμένες</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.pending_jobs}</p>
                    <p className="text-xs text-slate-500 uppercase">Εκκρεμείς</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <div className="animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <h2 className="font-chivo text-2xl font-bold text-slate-900 uppercase">
                  Όλες οι Εργασίες
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="trash-btn"
                    variant="outline"
                    onClick={() => { fetchTrash(); setTrashDialogOpen(true); }}
                    className="border-2 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash className="w-4 h-4 mr-2" />
                    Κάδος
                  </Button>
                  <Button
                    data-testid="export-excel-btn"
                    variant="outline"
                    onClick={handleExportExcel}
                    disabled={exportingExcel}
                    className="border-2"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    {exportingExcel ? 'Εξαγωγή...' : 'Excel'}
                  </Button>
                  <Button
                    data-testid="export-pdf-btn"
                    variant="outline"
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    className="border-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exportingPdf ? 'Εξαγωγή...' : 'PDF'}
                  </Button>
                  <Button
                    data-testid="send-report-btn"
                    variant="outline"
                    onClick={handleSendDailyReport}
                    disabled={sendingReport}
                    className="border-2 border-orange-500 text-orange-500 hover:bg-orange-50"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingReport ? 'Αποστολή...' : 'Email Αναφοράς'}
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τεχνικός</Label>
                    <Select value={filterTechnician} onValueChange={setFilterTechnician}>
                      <SelectTrigger data-testid="filter-technician" className="input-rugged">
                        <SelectValue placeholder="Όλοι" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Όλοι</SelectItem>
                        {technicians.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Από</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="input-rugged w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filterDateFrom ? format(filterDateFrom, 'dd/MM/yyyy') : 'Επιλογή'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={filterDateFrom} onSelect={setFilterDateFrom} locale={el} />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Έως</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="input-rugged w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filterDateTo ? format(filterDateTo, 'dd/MM/yyyy') : 'Επιλογή'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={filterDateTo} onSelect={setFilterDateTo} locale={el} />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Κατάσταση</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger data-testid="filter-status" className="input-rugged">
                        <SelectValue placeholder="Όλες" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Όλες</SelectItem>
                        <SelectItem value="pending">Εκκρεμείς</SelectItem>
                        <SelectItem value="completed">Ολοκληρωμένες</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Αναζήτηση</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        data-testid="filter-customer"
                        value={filterCustomer}
                        onChange={(e) => setFilterCustomer(e.target.value)}
                        className="input-rugged pl-10"
                        placeholder="Πελάτης, τηλ, διεύθυνση..."
                      />
                    </div>
                  </div>
                </div>

                {/* Sorting & Clear Filters Row */}
                <div className="flex flex-wrap items-center justify-between mt-4 gap-4">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-slate-500" />
                    <Label className="text-sm font-bold uppercase text-slate-500">Ταξινόμηση:</Label>
                    <Select value={sortOrder} onValueChange={setSortOrder}>
                      <SelectTrigger data-testid="sort-order" className="w-[200px] h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">Νεότερες πρώτα</SelectItem>
                        <SelectItem value="oldest">Παλαιότερες πρώτα</SelectItem>
                        <SelectItem value="date_desc">Ημ/νία επίσκεψης (↓)</SelectItem>
                        <SelectItem value="date_asc">Ημ/νία επίσκεψης (↑)</SelectItem>
                        <SelectItem value="customer_asc">Πελάτης (Α-Ω)</SelectItem>
                        <SelectItem value="customer_desc">Πελάτης (Ω-Α)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                    Καθαρισμός Φίλτρων
                  </Button>
                </div>
              </div>

              {/* Jobs Table */}
              <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ημερομηνία</th>
                        <th>Πελάτης</th>
                        <th>Διεύθυνση</th>
                        <th>Τεχνικός</th>
                        <th>Τύπος</th>
                        <th>Πληρωμή</th>
                        <th>Κατάσταση</th>
                        <th>Φωτογραφίες</th>
                        <th>Ενέργειες</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job) => (
                        <tr key={job.id}>
                          <td className="font-medium">{job.visit_date}</td>
                          <td>
                            <div>{job.customer_name}</div>
                            <div className="text-sm text-slate-500">{job.customer_phone}</div>
                          </td>
                          <td>
                            <div>{job.address_street}</div>
                            <div className="text-sm text-slate-500">{job.address_city} {job.address_postal}</div>
                          </td>
                          <td>{job.technician_name}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${job.job_type === 'wholesale' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                              {job.job_type === 'wholesale' ? <Store className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}
                              {job.job_type === 'wholesale' ? 'Χονδρική' : 'Λιανική'}
                            </span>
                          </td>
                          <td>
                            <span className="text-sm flex items-center gap-1">
                              {PAYMENT_METHODS[job.payment_method]?.label || job.payment_method}
                            </span>
                          </td>
                          <td>{getStatusBadge(job.status)}</td>
                          <td>
                            <span className="flex items-center gap-1 text-slate-600">
                              <Image className="w-4 h-4" />
                              {job.photos?.length || 0}
                            </span>
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                data-testid={`view-job-${job.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewJob(job.id)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Eye className="w-4 h-4 mr-1" /> Προβολή
                              </Button>
                              <Button
                                data-testid={`delete-job-${job.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteJob(job.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {jobs.length === 0 && (
                        <tr>
                          <td colSpan={9} className="text-center py-8 text-slate-500">
                            Δεν βρέθηκαν εργασίες
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <h2 className="font-chivo text-2xl font-bold text-slate-900 uppercase">
                  Διαχείριση Χρηστών
                </h2>
                <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="add-user-btn"
                      className="btn-orange"
                      onClick={() => {
                        setEditingUser(null);
                        setUserForm({ name: '', email: '', password: '', role: 'technician' });
                      }}
                    >
                      <UserPlus className="w-5 h-5 mr-2" /> Νέος Χρήστης
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-chivo uppercase">
                        {editingUser ? 'Επεξεργασία Χρήστη' : 'Νέος Χρήστης'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Όνομα</Label>
                        <Input
                          data-testid="user-name-input"
                          value={userForm.name}
                          onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                          className="input-rugged"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Email</Label>
                        <Input
                          data-testid="user-email-input"
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                          className="input-rugged"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">
                          {editingUser ? 'Νέος Κωδικός (προαιρετικό)' : 'Κωδικός'}
                        </Label>
                        <Input
                          data-testid="user-password-input"
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                          className="input-rugged"
                          required={!editingUser}
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Ρόλος</Label>
                        <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                          <SelectTrigger data-testid="user-role-select" className="input-rugged">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="technician">Τεχνικός</SelectItem>
                            <SelectItem value="admin">Διαχειριστής</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button data-testid="save-user-btn" type="submit" className="btn-rugged w-full">
                        {editingUser ? 'Αποθήκευση' : 'Δημιουργία'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Users Table */}
              <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Όνομα</th>
                        <th>Email</th>
                        <th>Ρόλος</th>
                        <th>Κατάσταση</th>
                        <th>Ενέργειες</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id}>
                          <td className="font-medium">{u.name}</td>
                          <td>{u.email}</td>
                          <td>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                              {u.role === 'admin' ? 'Διαχειριστής' : 'Τεχνικός'}
                            </span>
                          </td>
                          <td>
                            <span className={u.active ? 'badge-active' : 'badge-inactive'}>
                              {u.active ? 'Ενεργός' : 'Ανενεργός'}
                            </span>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                data-testid={`edit-user-${u.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditUser(u)}
                                className="text-blue-600"
                                title="Επεξεργασία"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {u.id !== user?.id && (
                                <>
                                  <Button
                                    data-testid={`deactivate-user-${u.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeactivateUser(u.id)}
                                    className="text-amber-600"
                                    title="Απενεργοποίηση"
                                  >
                                    <UserX className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    data-testid={`delete-user-${u.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePermanentDeleteUser(u.id)}
                                    className="text-red-600"
                                    title="Οριστική Διαγραφή"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Assignments Tab */}
          {activeTab === 'assignments' && (
            <div className="animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <h2 className="font-chivo text-2xl font-bold text-slate-900 uppercase">
                  Αναθέσεις Εργασιών
                </h2>
                <div className="flex gap-2">
                  <Button
                    data-testid="migrate-assignments-btn"
                    variant="outline"
                    onClick={handleMigrateCompletedAssignments}
                    className="border-2 border-green-500 text-green-600 hover:bg-green-50"
                    title="Μεταφορά παλιών ολοκληρωμένων αναθέσεων στις εργασίες"
                  >
                    <ArrowUpDown className="w-4 h-4 mr-2" /> Μεταφορά Ολοκληρωμένων
                  </Button>
                  <Button
                    data-testid="new-assignment-btn"
                    className="btn-orange"
                    onClick={() => setAssignmentDialogOpen(true)}
                  >
                    <Send className="w-5 h-5 mr-2" /> Νέα Ανάθεση
                  </Button>
                </div>
              </div>

              {/* Assignments List */}
              {assignments.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border-2 border-slate-200">
                  <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Δεν υπάρχουν αναθέσεις</p>
                  <Button
                    data-testid="create-first-assignment-btn"
                    className="btn-orange mt-4"
                    onClick={() => setAssignmentDialogOpen(true)}
                  >
                    <Plus className="w-5 h-5 mr-2" /> Δημιουργία Ανάθεσης
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Ημερομηνία</th>
                          <th>Πελάτης</th>
                          <th>Διεύθυνση</th>
                          <th>Τεχνικός</th>
                          <th>Προτεραιότητα</th>
                          <th>Κατάσταση</th>
                          <th>Ενέργειες</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map((assignment) => (
                          <tr key={assignment.id}>
                            <td className="font-medium">
                              <div>{assignment.visit_date}</div>
                              {assignment.visit_time && (
                                <div className="text-xs text-blue-600 font-bold">{assignment.visit_time}</div>
                              )}
                            </td>
                            <td>
                              <div>{assignment.customer_name}</div>
                              <div className="text-xs text-slate-500">{assignment.customer_phone}</div>
                            </td>
                            <td>
                              <div>{assignment.address_street}</div>
                              <div className="text-xs text-slate-500">{assignment.address_city}</div>
                            </td>
                            <td>
                              {assignment.technician_names && assignment.technician_names.length > 1 ? (
                                <div className="flex items-center gap-1">
                                  <Users2 className="w-4 h-4 text-blue-500" />
                                  <span>{assignment.technician_names.join(', ')}</span>
                                </div>
                              ) : (
                                assignment.technician_name || (assignment.technician_names && assignment.technician_names[0])
                              )}
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                assignment.priority === 'high' ? 'bg-red-100 text-red-700' :
                                assignment.priority === 'low' ? 'bg-slate-100 text-slate-600' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {assignment.priority === 'high' ? 'Υψηλή' : 
                                 assignment.priority === 'low' ? 'Χαμηλή' : 'Κανονική'}
                              </span>
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                assignment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {assignment.status === 'completed' ? 'Ολοκληρώθηκε' : 
                                 assignment.status === 'in_progress' ? 'Σε εξέλιξη' : 'Ανατέθηκε'}
                              </span>
                            </td>
                            <td>
                              <div className="flex gap-1">
                                <Button
                                  data-testid={`edit-assignment-${assignment.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditAssignment(assignment)}
                                  className="text-blue-600"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`delete-assignment-${assignment.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAssignment(assignment.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Assignment Creation/Edit Dialog */}
      <Dialog open={assignmentDialogOpen} onOpenChange={(open) => {
        setAssignmentDialogOpen(open);
        if (!open) {
          setEditingAssignment(null);
          setAssignmentForm({
            customer_name: '',
            customer_phone: '',
            address_street: '',
            address_city: '',
            address_postal: '',
            visit_date: new Date(),
            visit_time: '',
            description: '',
            technician_ids: [],
            priority: 'normal',
            notes: '',
            job_type: 'retail',
            payment_method: 'cash',
            payment_comments: '',
            wholesale_customer_name: '',
            wholesale_customer_phone: '',
            needs_invoice: false,
            customer_afm: '',
            customer_email: ''
          });
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">
              {editingAssignment ? 'Επεξεργασία Ανάθεσης' : 'Νέα Ανάθεση Εργασίας'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAssignment} className="space-y-4 mt-4">
            {/* Technician Selection - Multiple */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">
                Τεχνικοί * {assignmentForm.technician_ids.length > 0 && `(${assignmentForm.technician_ids.length} επιλεγμένοι)`}
              </Label>
              <div className="border-2 border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {technicians.map((tech) => (
                  <label key={tech.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assignmentForm.technician_ids.includes(tech.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssignmentForm({ 
                            ...assignmentForm, 
                            technician_ids: [...assignmentForm.technician_ids, tech.id] 
                          });
                        } else {
                          setAssignmentForm({ 
                            ...assignmentForm, 
                            technician_ids: assignmentForm.technician_ids.filter(id => id !== tech.id) 
                          });
                        }
                      }}
                      className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="font-medium">{tech.name}</span>
                  </label>
                ))}
                {technicians.length === 0 && (
                  <p className="text-slate-500 text-sm">Δεν υπάρχουν διαθέσιμοι τεχνικοί</p>
                )}
              </div>
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Πελάτης *</Label>
                <Input
                  data-testid="assignment-customer-name"
                  value={assignmentForm.customer_name}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, customer_name: e.target.value })}
                  className="input-rugged"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τηλέφωνο *</Label>
                <Input
                  data-testid="assignment-customer-phone"
                  value={assignmentForm.customer_phone}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, customer_phone: e.target.value })}
                  className="input-rugged"
                  required
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Διεύθυνση *</Label>
              <Input
                data-testid="assignment-address-street"
                value={assignmentForm.address_street}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, address_street: e.target.value })}
                className="input-rugged"
                placeholder="Οδός"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  data-testid="assignment-address-city"
                  value={assignmentForm.address_city}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, address_city: e.target.value })}
                  className="input-rugged"
                  placeholder="Πόλη"
                  required
                />
              </div>
              <div>
                <Input
                  data-testid="assignment-address-postal"
                  value={assignmentForm.address_postal}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, address_postal: e.target.value })}
                  className="input-rugged"
                  placeholder="Τ.Κ."
                />
              </div>
            </div>

            {/* Visit Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Ημερομηνία *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="assignment-visit-date"
                      variant="outline"
                      className="input-rugged w-full justify-start text-left"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(assignmentForm.visit_date, 'dd MMM yyyy', { locale: el })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={assignmentForm.visit_date}
                      onSelect={(date) => date && setAssignmentForm({ ...assignmentForm, visit_date: date })}
                      locale={el}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Ώρα</Label>
                <Input
                  data-testid="assignment-visit-time"
                  type="time"
                  value={assignmentForm.visit_time}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, visit_time: e.target.value })}
                  className="input-rugged"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Περιγραφή Εργασίας *</Label>
              <Textarea
                data-testid="assignment-description"
                value={assignmentForm.description}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })}
                className="input-rugged min-h-[100px]"
                placeholder="Περιγράψτε τι πρέπει να γίνει..."
                required
              />
            </div>

            {/* Priority */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Προτεραιότητα</Label>
              <Select value={assignmentForm.priority} onValueChange={(v) => setAssignmentForm({ ...assignmentForm, priority: v })}>
                <SelectTrigger data-testid="assignment-priority-select" className="input-rugged">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Χαμηλή</SelectItem>
                  <SelectItem value="normal">Κανονική</SelectItem>
                  <SelectItem value="high">Υψηλή</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Σημειώσεις (προαιρετικό)</Label>
              <Textarea
                data-testid="assignment-notes"
                value={assignmentForm.notes}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                className="input-rugged"
                placeholder="Επιπλέον σημειώσεις για τον τεχνικό..."
              />
            </div>

            {/* Payment Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-chivo font-bold text-slate-900 uppercase mb-4">Πληροφορίες Πληρωμής</h4>
              
              {/* Job Type */}
              <div className="mb-4">
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τύπος Εργασίας</Label>
                <Select value={assignmentForm.job_type} onValueChange={(v) => setAssignmentForm({ ...assignmentForm, job_type: v, wholesale_customer_name: '', wholesale_customer_phone: '' })}>
                  <SelectTrigger data-testid="assignment-job-type" className="input-rugged">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="w-4 h-4" /> Λιανική
                      </div>
                    </SelectItem>
                    <SelectItem value="wholesale">
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4" /> Χονδρική
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Wholesale Customer Info - Only shown when job_type is wholesale */}
              {assignmentForm.job_type === 'wholesale' && (
                <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h5 className="text-sm font-bold uppercase text-purple-700 mb-3 flex items-center gap-2">
                    <Store className="w-4 h-4" /> Στοιχεία Πελάτη Χονδρικής
                  </h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Ονοματεπώνυμο</Label>
                      <Input
                        data-testid="assignment-wholesale-name"
                        value={assignmentForm.wholesale_customer_name}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, wholesale_customer_name: e.target.value })}
                        className="input-rugged"
                        placeholder="Πελάτης χονδρικής"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τηλέφωνο (προαιρ.)</Label>
                      <Input
                        data-testid="assignment-wholesale-phone"
                        value={assignmentForm.wholesale_customer_phone}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, wholesale_customer_phone: e.target.value })}
                        className="input-rugged"
                        placeholder="Τηλέφωνο"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method */}
              <div className="mb-4">
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τρόπος Πληρωμής</Label>
                <Select value={assignmentForm.payment_method} onValueChange={(v) => setAssignmentForm({ ...assignmentForm, payment_method: v })}>
                  <SelectTrigger data-testid="assignment-payment-method" className="input-rugged">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4" /> Μετρητά
                      </div>
                    </SelectItem>
                    <SelectItem value="card">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" /> Κάρτα
                      </div>
                    </SelectItem>
                    <SelectItem value="iris">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-4 h-4" /> IRIS
                      </div>
                    </SelectItem>
                    <SelectItem value="deposit">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4" /> Κατάθεση
                      </div>
                    </SelectItem>
                    <SelectItem value="pending_deposit">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Αναμονή Κατάθεσης
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Comments */}
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Σχόλια Πληρωμής</Label>
                <Input
                  data-testid="assignment-payment-comments"
                  value={assignmentForm.payment_comments}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, payment_comments: e.target.value })}
                  className="input-rugged"
                  placeholder="π.χ. Τιμοκατάλογος, έκπτωση, κ.λπ."
                />
              </div>

              {/* Invoice Checkbox */}
              <div className="mt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={assignmentForm.needs_invoice}
                    onChange={(e) => setAssignmentForm({ 
                      ...assignmentForm, 
                      needs_invoice: e.target.checked,
                      customer_afm: e.target.checked ? assignmentForm.customer_afm : '',
                      customer_email: e.target.checked ? assignmentForm.customer_email : ''
                    })}
                    className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    data-testid="assignment-needs-invoice"
                  />
                  <span className="font-medium text-slate-700">Χρειάζεται Τιμολόγιο</span>
                </label>
              </div>

              {/* Invoice Fields - Only shown when needs_invoice is true */}
              {assignmentForm.needs_invoice && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mt-2">
                  <h5 className="text-sm font-bold uppercase text-blue-700 mb-3">Στοιχεία Τιμολογίου</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">ΑΦΜ</Label>
                      <Input
                        data-testid="assignment-customer-afm"
                        value={assignmentForm.customer_afm}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, customer_afm: e.target.value })}
                        className="input-rugged"
                        placeholder="ΑΦΜ πελάτη"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Email</Label>
                      <Input
                        data-testid="assignment-customer-email"
                        type="email"
                        value={assignmentForm.customer_email}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, customer_email: e.target.value })}
                        className="input-rugged"
                        placeholder="Email πελάτη"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button
              data-testid="submit-assignment-btn"
              type="submit"
              className="btn-orange w-full"
              disabled={creatingAssignment}
            >
              {creatingAssignment ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" /> {editingAssignment ? 'Αποθήκευση...' : 'Δημιουργία...'}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" /> {editingAssignment ? 'Αποθήκευση Αλλαγών' : 'Δημιουργία & Αποστολή Ειδοποίησης'}
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Job Detail Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-chivo uppercase">Λεπτομέρειες Εργασίας</DialogTitle>
              {selectedJob && (
                <Button
                  data-testid="edit-job-from-details-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => { setJobDialogOpen(false); handleEditJob(selectedJob); }}
                  className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <Edit className="w-4 h-4 mr-1" /> Επεξεργασία
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-6 mt-4">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${selectedJob.status === 'completed' ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold uppercase text-slate-500">Κατάσταση</p>
                    <div className="mt-1">{getStatusBadge(selectedJob.status)}</div>
                  </div>
                  <Select 
                    value={selectedJob.status} 
                    onValueChange={(v) => handleUpdateJobStatus(selectedJob.id, v)}
                  >
                    <SelectTrigger data-testid="job-status-select" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Εκκρεμής</SelectItem>
                      <SelectItem value="completed">Ολοκληρώθηκε</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Status Notes */}
                {selectedJob.status === 'pending' && selectedJob.status_notes && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-amber-300">
                    <p className="text-xs font-bold uppercase text-amber-700 mb-1">Σχόλια Εκκρεμότητας</p>
                    <p className="text-sm text-slate-700">{selectedJob.status_notes}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500">Πελάτης</p>
                  <p className="font-medium">{selectedJob.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500">Τηλέφωνο</p>
                  <p className="font-medium">{selectedJob.customer_phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-bold uppercase text-slate-500">Διεύθυνση</p>
                  <p className="font-medium">{selectedJob.address_street}, {selectedJob.address_city} {selectedJob.address_postal}</p>
                </div>
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500">Ημερομηνία</p>
                  <p className="font-medium">{selectedJob.visit_date}</p>
                </div>
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500">Τεχνικός</p>
                  <p className="font-medium">{selectedJob.technician_name}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-bold uppercase text-slate-500 mb-2">Περιγραφή</p>
                <p className="bg-slate-50 p-4 rounded-lg">{selectedJob.description || '-'}</p>
              </div>

              <div>
                <p className="text-sm font-bold uppercase text-slate-500 mb-2">Εργασία που εκτελέστηκε</p>
                <p className="bg-slate-50 p-4 rounded-lg">{selectedJob.work_performed || '-'}</p>
              </div>

              <div>
                <p className="text-sm font-bold uppercase text-slate-500 mb-2">Συστάσεις</p>
                <p className="bg-slate-50 p-4 rounded-lg">{selectedJob.recommendations || '-'}</p>
              </div>

              {/* Job Type & Payment Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500">Τύπος Δουλειάς</p>
                  <p className="font-medium flex items-center gap-2 mt-1">
                    {selectedJob.job_type === 'wholesale' ? (
                      <><Store className="w-4 h-4 text-purple-500" /> Χονδρική</>
                    ) : (
                      <><ShoppingBag className="w-4 h-4 text-blue-500" /> Λιανική</>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500">Μέθοδος Πληρωμής</p>
                  <p className="font-medium flex items-center gap-2 mt-1">
                    {PAYMENT_METHODS[selectedJob.payment_method]?.icon && (
                      (() => {
                        const Icon = PAYMENT_METHODS[selectedJob.payment_method].icon;
                        return <Icon className="w-4 h-4" />;
                      })()
                    )}
                    {PAYMENT_METHODS[selectedJob.payment_method]?.label || selectedJob.payment_method}
                  </p>
                </div>
              </div>

              {selectedJob.payment_comments && (
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500 mb-2">Σχόλια Πληρωμής</p>
                  <p className="bg-amber-50 p-4 rounded-lg border border-amber-200">{selectedJob.payment_comments}</p>
                </div>
              )}

              {/* Wholesale Customer Info */}
              {selectedJob.job_type === 'wholesale' && selectedJob.wholesale_customer_name && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm font-bold uppercase text-purple-700 mb-2">Πελάτης Χονδρικής</p>
                  <div className="flex gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Ονοματεπώνυμο</p>
                      <p className="font-medium">{selectedJob.wholesale_customer_name}</p>
                    </div>
                    {selectedJob.wholesale_customer_phone && (
                      <div>
                        <p className="text-sm text-slate-500">Τηλέφωνο</p>
                        <p className="font-medium">{selectedJob.wholesale_customer_phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Info */}
              {selectedJob.needs_invoice && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-bold uppercase text-blue-700 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4" /> Χρειάζεται Τιμολόγιο
                  </p>
                  <div className="flex gap-6">
                    {selectedJob.customer_afm && (
                      <div>
                        <p className="text-sm text-slate-500">ΑΦΜ</p>
                        <p className="font-medium">{selectedJob.customer_afm}</p>
                      </div>
                    )}
                    {selectedJob.customer_email && (
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <p className="font-medium">{selectedJob.customer_email}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedJob.photos && selectedJob.photos.length > 0 && (
                <div>
                  <p className="text-sm font-bold uppercase text-slate-500 mb-2">Φωτογραφίες ({selectedJob.photos.length})</p>
                  <div className="photo-grid">
                    {selectedJob.photos.map((photo) => (
                      <a
                        key={photo.id}
                        href={`${getPhotoUrl(photo.file_path)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={`${getPhotoUrl(photo.file_path)}`}
                          alt="Job photo"
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={editJobDialogOpen} onOpenChange={setEditJobDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">Επεξεργασία Εργασίας</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveJob} className="space-y-4 mt-4">
            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Πελάτης</Label>
                <Input
                  data-testid="edit-job-customer-name"
                  value={editJobForm.customer_name}
                  onChange={(e) => setEditJobForm({ ...editJobForm, customer_name: e.target.value })}
                  className="input-rugged"
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τηλέφωνο</Label>
                <Input
                  data-testid="edit-job-customer-phone"
                  value={editJobForm.customer_phone}
                  onChange={(e) => setEditJobForm({ ...editJobForm, customer_phone: e.target.value })}
                  className="input-rugged"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Διεύθυνση</Label>
              <Input
                data-testid="edit-job-address-street"
                value={editJobForm.address_street}
                onChange={(e) => setEditJobForm({ ...editJobForm, address_street: e.target.value })}
                className="input-rugged mb-2"
                placeholder="Οδός"
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  data-testid="edit-job-address-city"
                  value={editJobForm.address_city}
                  onChange={(e) => setEditJobForm({ ...editJobForm, address_city: e.target.value })}
                  className="input-rugged"
                  placeholder="Πόλη"
                />
                <Input
                  data-testid="edit-job-address-postal"
                  value={editJobForm.address_postal}
                  onChange={(e) => setEditJobForm({ ...editJobForm, address_postal: e.target.value })}
                  className="input-rugged"
                  placeholder="Τ.Κ."
                />
              </div>
            </div>

            {/* Date */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Ημερομηνία</Label>
              <Input
                data-testid="edit-job-visit-date"
                type="date"
                value={editJobForm.visit_date}
                onChange={(e) => setEditJobForm({ ...editJobForm, visit_date: e.target.value })}
                className="input-rugged"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Περιγραφή</Label>
              <Textarea
                data-testid="edit-job-description"
                value={editJobForm.description}
                onChange={(e) => setEditJobForm({ ...editJobForm, description: e.target.value })}
                className="input-rugged"
              />
            </div>

            {/* Work Performed */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Εργασία που εκτελέστηκε</Label>
              <Textarea
                data-testid="edit-job-work-performed"
                value={editJobForm.work_performed}
                onChange={(e) => setEditJobForm({ ...editJobForm, work_performed: e.target.value })}
                className="input-rugged"
              />
            </div>

            {/* Recommendations */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Προτάσεις/Σημειώσεις</Label>
              <Textarea
                data-testid="edit-job-recommendations"
                value={editJobForm.recommendations}
                onChange={(e) => setEditJobForm({ ...editJobForm, recommendations: e.target.value })}
                className="input-rugged"
              />
            </div>

            {/* Status */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Κατάσταση</Label>
              <Select value={editJobForm.status} onValueChange={(v) => setEditJobForm({ ...editJobForm, status: v })}>
                <SelectTrigger data-testid="edit-job-status" className="input-rugged">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Εκκρεμής</SelectItem>
                  <SelectItem value="completed">Ολοκληρώθηκε</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editJobForm.status === 'pending' && (
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Σημειώσεις Κατάστασης</Label>
                <Input
                  data-testid="edit-job-status-notes"
                  value={editJobForm.status_notes}
                  onChange={(e) => setEditJobForm({ ...editJobForm, status_notes: e.target.value })}
                  className="input-rugged"
                  placeholder="Λόγος εκκρεμότητας..."
                />
              </div>
            )}

            {/* Job Type */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τύπος Εργασίας</Label>
              <Select value={editJobForm.job_type} onValueChange={(v) => setEditJobForm({ ...editJobForm, job_type: v })}>
                <SelectTrigger data-testid="edit-job-type" className="input-rugged">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="retail">Λιανική</SelectItem>
                  <SelectItem value="wholesale">Χονδρική</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Wholesale Customer Info */}
            {editJobForm.job_type === 'wholesale' && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h5 className="text-sm font-bold uppercase text-purple-700 mb-3 flex items-center gap-2">
                  <Store className="w-4 h-4" /> Πελάτης Χονδρικής
                </h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Ονοματεπώνυμο</Label>
                    <Input
                      data-testid="edit-job-wholesale-name"
                      value={editJobForm.wholesale_customer_name}
                      onChange={(e) => setEditJobForm({ ...editJobForm, wholesale_customer_name: e.target.value })}
                      className="input-rugged"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τηλέφωνο</Label>
                    <Input
                      data-testid="edit-job-wholesale-phone"
                      value={editJobForm.wholesale_customer_phone}
                      onChange={(e) => setEditJobForm({ ...editJobForm, wholesale_customer_phone: e.target.value })}
                      className="input-rugged"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Payment Method */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τρόπος Πληρωμής</Label>
              <Select value={editJobForm.payment_method} onValueChange={(v) => setEditJobForm({ ...editJobForm, payment_method: v })}>
                <SelectTrigger data-testid="edit-job-payment" className="input-rugged">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Μετρητά</SelectItem>
                  <SelectItem value="card">Κάρτα</SelectItem>
                  <SelectItem value="iris">Iris</SelectItem>
                  <SelectItem value="deposit">Κατάθεση</SelectItem>
                  <SelectItem value="pending_deposit">Αναμονή Κατάθεσης</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment Comments */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Σχόλια Πληρωμής</Label>
              <Input
                data-testid="edit-job-payment-comments"
                value={editJobForm.payment_comments}
                onChange={(e) => setEditJobForm({ ...editJobForm, payment_comments: e.target.value })}
                className="input-rugged"
              />
            </div>

            {/* Invoice Checkbox */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editJobForm.needs_invoice}
                  onChange={(e) => setEditJobForm({ 
                    ...editJobForm, 
                    needs_invoice: e.target.checked,
                    customer_afm: e.target.checked ? editJobForm.customer_afm : '',
                    customer_email: e.target.checked ? editJobForm.customer_email : ''
                  })}
                  className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  data-testid="edit-job-needs-invoice"
                />
                <span className="font-medium text-slate-700">Χρειάζεται Τιμολόγιο</span>
              </label>
            </div>

            {/* Invoice Fields */}
            {editJobForm.needs_invoice && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="text-sm font-bold uppercase text-blue-700 mb-3">Στοιχεία Τιμολογίου</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">ΑΦΜ</Label>
                    <Input
                      data-testid="edit-job-customer-afm"
                      value={editJobForm.customer_afm}
                      onChange={(e) => setEditJobForm({ ...editJobForm, customer_afm: e.target.value })}
                      className="input-rugged"
                      placeholder="ΑΦΜ πελάτη"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Email</Label>
                    <Input
                      data-testid="edit-job-customer-email"
                      type="email"
                      value={editJobForm.customer_email}
                      onChange={(e) => setEditJobForm({ ...editJobForm, customer_email: e.target.value })}
                      className="input-rugged"
                      placeholder="Email πελάτη"
                    />
                  </div>
                </div>
              </div>
            )}

            <Button
              data-testid="save-job-btn"
              type="submit"
              className="btn-rugged w-full"
            >
              <Check className="w-5 h-5 mr-2" /> Αποθήκευση Αλλαγών
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Trash Dialog */}
      <Dialog open={trashDialogOpen} onOpenChange={setTrashDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase flex items-center gap-2">
              <Trash className="w-5 h-5 text-red-500" /> Κάδος Απορριμμάτων
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {trashJobs.length === 0 ? (
              <div className="text-center py-12">
                <Trash className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Ο κάδος είναι άδειος</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  {trashJobs.length} εργασί{trashJobs.length === 1 ? 'α' : 'ες'} στον κάδο
                </p>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ημερομηνία</th>
                        <th>Πελάτης</th>
                        <th>Τεχνικός</th>
                        <th>Ενέργειες</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trashJobs.map((job) => (
                        <tr key={job.id}>
                          <td>{job.visit_date}</td>
                          <td>
                            <div>{job.customer_name}</div>
                            <div className="text-xs text-slate-500">{job.customer_phone}</div>
                          </td>
                          <td>{job.technician_name}</td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                data-testid={`restore-job-${job.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestoreJob(job.id)}
                                className="text-green-600 hover:text-green-700"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" /> Επαναφορά
                              </Button>
                              <Button
                                data-testid={`permanent-delete-job-${job.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePermanentDeleteJob(job.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Οριστική Διαγραφή
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
